import React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { UnifiedTable, UnifiedTableColumn } from "@/components/ui/unifiedTable/UnifiedTable";
import {
  ArrowRight,
  CheckCircle2,
  Filter,
  LayoutDashboard,
  List,
  Pencil,
  Search,
  ShieldCheck,
  UserRound,
  XCircle,
} from "lucide-react";

interface StyleGuideDemoRow {
  id: string;
  participant: string;
  program: string;
  owner: string;
  status: "Ready" | "Needs Review" | "Escalated";
}

const styleGuideDemoRows: StyleGuideDemoRow[] = [
  { id: "200011", participant: "Maya Patel", program: "ZODR", owner: "L. Romero", status: "Ready" },
  { id: "200045", participant: "Elias Kim", program: "MIST", owner: "S. Nash", status: "Needs Review" },
  { id: "200089", participant: "Harper Chen", program: "LEAD", owner: "T. Alvarez", status: "Escalated" },
];

const codeExample = `import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search } from "lucide-react";

<div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_220px_auto]">
  <Input id="candidate-list-search-input" placeholder="Search participants" />
  <Select defaultValue="active">
    <SelectTrigger id="candidate-list-status-select">
      <SelectValue placeholder="Status" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="active">Active</SelectItem>
      <SelectItem value="pending">Pending</SelectItem>
    </SelectContent>
  </Select>
  <Button id="candidate-list-search-button" size="sm" type="button">
    <Search className="h-4 w-4" />
    Search
  </Button>
</div>`;

const statusClassName = (status: StyleGuideDemoRow["status"]): string => {
  if (status === "Ready") {
    return "border-[var(--semantic-success)]/40 bg-[var(--semantic-success)]/12 text-[var(--neutral-gray-120)]";
  }

  if (status === "Needs Review") {
    return "border-[var(--semantic-warning)]/40 bg-[var(--semantic-warning)]/12 text-[var(--neutral-gray-120)]";
  }

  return "border-[var(--semantic-error)]/40 bg-[var(--semantic-error)]/10 text-[var(--neutral-gray-120)]";
};

const styleGuideColumns: UnifiedTableColumn<StyleGuideDemoRow>[] = [
  {
    key: "participant",
    header: "Participant",
    field: "participant",
    sortable: true,
  },
  {
    key: "program",
    header: "Program",
    field: "program",
    sortable: true,
  },
  {
    key: "owner",
    header: "Assigned Owner",
    field: "owner",
    sortable: true,
  },
  {
    key: "status",
    header: "Status",
    sortable: true,
    searchValue: (row) => row.status,
    accessor: (row) => (
      <span
        className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${statusClassName(row.status)}`}
      >
        {row.status}
      </span>
    ),
  },
  {
    key: "actions",
    header: "Actions",
    align: "right",
    accessor: (row) => (
      <Button
        id={`ui-style-guide-row-open-${row.id}-button`}
        type="button"
        variant="ghost"
        size="sm"
        className="text-[var(--azure-primary)] hover:bg-[var(--surface-muted)] hover:text-[var(--azure-primary)]"
      >
        <ArrowRight className="h-4 w-4" />
        Open
      </Button>
    ),
  },
];

export const UiStyleGuideDemoPage = () => {
  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-6">
      <Card>
        <CardHeader className="gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className="border-[var(--azure-primary)]/30 bg-[var(--azure-primary)]/8 px-3 py-1 text-[var(--azure-primary)]"
            >
              Canonical Demo
            </Badge>
            <Badge
              variant="outline"
              className="border-[var(--neutral-gray-40)] bg-[var(--surface-muted)] px-3 py-1 text-[var(--neutral-gray-100)]"
            >
              docs/frontend/unified_ui_style_guide.md
            </Badge>
          </div>
          <div className="space-y-2">
            <CardTitle className="text-2xl">Unified UI Style Guide Demo</CardTitle>
            <CardDescription className="max-w-3xl text-sm text-[var(--neutral-gray-100)]">
              This page turns the written style guide into a working reference. Build new screens from the dashboard
              shell, the candidate-list workbench pattern, and the participant-profile information rhythm.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-3">
          <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] p-4">
            <div className="mb-3 flex items-center gap-2 text-[var(--neutral-gray-120)]">
              <LayoutDashboard className="h-4 w-4 text-[var(--azure-primary)]" />
              <span className="text-sm font-semibold">Dashboard</span>
            </div>
            <p className="text-sm text-[var(--neutral-gray-100)]">
              Use it for the shell, page rhythm, alert treatment, and restrained blue action language.
            </p>
          </div>
          <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] p-4">
            <div className="mb-3 flex items-center gap-2 text-[var(--neutral-gray-120)]">
              <List className="h-4 w-4 text-[var(--azure-primary)]" />
              <span className="text-sm font-semibold">Candidate List</span>
            </div>
            <p className="text-sm text-[var(--neutral-gray-100)]">
              Use it for compact filters, dense tables, right-side row actions, and workbench toolbars.
            </p>
          </div>
          <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] p-4">
            <div className="mb-3 flex items-center gap-2 text-[var(--neutral-gray-120)]">
              <UserRound className="h-4 w-4 text-[var(--azure-primary)]" />
              <span className="text-sm font-semibold">Participant Profile</span>
            </div>
            <p className="text-sm text-[var(--neutral-gray-100)]">
              Use it for grouped data presentation, label/value density, and quiet information hierarchy.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <Card>
          <CardHeader>
            <CardTitle>Reference Order</CardTitle>
            <CardDescription>When a page drifts, pull it back to this order instead of inventing a new pattern.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] px-4 py-3">
              <p className="text-sm font-semibold text-[var(--neutral-gray-120)]">1. Shared primitives and Azure tokens</p>
              <p className="text-sm text-[var(--neutral-gray-100)]">Use shared `Button`, `Input`, `Select`, `Textarea`, `Card`, and `UnifiedTable` first.</p>
            </div>
            <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] px-4 py-3">
              <p className="text-sm font-semibold text-[var(--neutral-gray-120)]">2. Dashboard shell and color rhythm</p>
              <p className="text-sm text-[var(--neutral-gray-100)]">Keep soft neutral surfaces and blue reserved for action and selected emphasis.</p>
            </div>
            <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] px-4 py-3">
              <p className="text-sm font-semibold text-[var(--neutral-gray-120)]">3. Candidate list density and row actions</p>
              <p className="text-sm text-[var(--neutral-gray-100)]">Filters stay grouped, tables stay dense, and row actions stay on the right.</p>
            </div>
            <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] px-4 py-3">
              <p className="text-sm font-semibold text-[var(--neutral-gray-120)]">4. Participant profile data grouping</p>
              <p className="text-sm text-[var(--neutral-gray-100)]">Use its label/value clarity, but not its older hardcoded colors.</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Hard Rules</CardTitle>
            <CardDescription>These are the non-negotiables for the Microsoft-style government workbench.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] p-4">
              <ShieldCheck className="mt-0.5 h-4 w-4 text-[var(--azure-primary)]" />
              <p className="text-sm text-[var(--neutral-gray-100)]">Controls fill their assigned container. No dead space inside field shells.</p>
            </div>
            <div className="flex items-start gap-3 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] p-4">
              <ShieldCheck className="mt-0.5 h-4 w-4 text-[var(--azure-primary)]" />
              <p className="text-sm text-[var(--neutral-gray-100)]">Buttons and fields stay compact by default. Taller controls should be deliberate exceptions.</p>
            </div>
            <div className="flex items-start gap-3 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] p-4">
              <ShieldCheck className="mt-0.5 h-4 w-4 text-[var(--azure-primary)]" />
              <p className="text-sm text-[var(--neutral-gray-100)]">Bold is used sparingly. One strong item per cluster is usually enough.</p>
            </div>
            <div className="flex items-start gap-3 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] p-4">
              <ShieldCheck className="mt-0.5 h-4 w-4 text-[var(--azure-primary)]" />
              <p className="text-sm text-[var(--neutral-gray-100)]">Primary actions are filled blue. Secondary actions are outline or muted.</p>
            </div>
            <div className="flex items-start gap-3 rounded-[var(--border-radius-medium)] border border-[var(--semantic-warning)]/30 bg-[var(--semantic-warning)]/8 p-4">
              <XCircle className="mt-0.5 h-4 w-4 text-[var(--semantic-warning)]" />
              <p className="text-sm text-[var(--neutral-gray-100)]">Do not copy the housing dashboard button treatment.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Compact Workbench Toolbar</CardTitle>
          <CardDescription>
            Use this pattern for dashboard and candidate-list style filters. Search stays wide. Filter controls fill their own slot.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_220px_220px_auto_auto]">
            <Input id="ui-style-guide-search-input" placeholder="Search by participant, booking number, or owner" />
            <Select defaultValue="ready">
              <SelectTrigger id="ui-style-guide-status-select">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ready">Ready</SelectItem>
                <SelectItem value="needs-review">Needs Review</SelectItem>
                <SelectItem value="escalated">Escalated</SelectItem>
              </SelectContent>
            </Select>
            <Select defaultValue="all-programs">
              <SelectTrigger id="ui-style-guide-program-select">
                <SelectValue placeholder="Program" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-programs">All Programs</SelectItem>
                <SelectItem value="zodr">ZODR</SelectItem>
                <SelectItem value="mist">MIST</SelectItem>
                <SelectItem value="lead">LEAD</SelectItem>
              </SelectContent>
            </Select>
            <Button id="ui-style-guide-search-button" type="button" size="sm">
              <Search className="h-4 w-4" />
              Search
            </Button>
            <Button id="ui-style-guide-reset-button" type="button" variant="outline" size="sm">
              <Filter className="h-4 w-4" />
              Reset
            </Button>
          </div>
          <p className="text-sm text-[var(--neutral-gray-100)]">
            This is the preferred control density for high-frequency work queues. Shared controls should default to `h-8`, and `size="sm"` is only for tighter button padding.
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Standard Form Block</CardTitle>
            <CardDescription>Forms stay quiet: full-width fields, restrained labels, and one clear primary action.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ui-style-guide-participant-input">Participant Name</Label>
                <Input id="ui-style-guide-participant-input" defaultValue="Maya Patel" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ui-style-guide-owner-input">Assigned Owner</Label>
                <Input id="ui-style-guide-owner-input" defaultValue="L. Romero" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ui-style-guide-program-form-select">Program</Label>
                <Select defaultValue="zodr">
                  <SelectTrigger id="ui-style-guide-program-form-select">
                    <SelectValue placeholder="Program" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="zodr">ZODR</SelectItem>
                    <SelectItem value="mist">MIST</SelectItem>
                    <SelectItem value="lead">LEAD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ui-style-guide-status-form-select">Disposition</Label>
                <Select defaultValue="review">
                  <SelectTrigger id="ui-style-guide-status-form-select">
                    <SelectValue placeholder="Disposition" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="review">Pending Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="hold">On Hold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="ui-style-guide-summary-textarea">Case Summary</Label>
                <Textarea
                  id="ui-style-guide-summary-textarea"
                  rows={4}
                  defaultValue="Use shared surfaces, keep labels calm, and let the form controls fill the full width of the grid cell."
                />
              </div>
            </div>
            <Separator className="bg-[var(--neutral-gray-30)]" />
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button id="ui-style-guide-cancel-button" type="button" variant="outline">
                <XCircle className="h-4 w-4" />
                Cancel
              </Button>
              <Button id="ui-style-guide-save-button" type="button">
                <CheckCircle2 className="h-4 w-4" />
                Save Changes
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Candidate-Style List Example</CardTitle>
            <CardDescription>Row actions stay on the right. Surfaces stay white or near-white. Density stays compact.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <UnifiedTable<StyleGuideDemoRow>
              columns={styleGuideColumns}
              data={styleGuideDemoRows}
              rowKey={(row) => row.id}
              searchEnabled={false}
              variant="striped"
              density="compact"
              toolbarLeft={
                <p className="text-sm text-[var(--neutral-gray-100)]">
                  Use the candidate list as the table baseline for operational pages.
                </p>
              }
              toolbarRight={
                <div className="flex flex-wrap gap-2">
                  <Button id="ui-style-guide-table-filter-button" type="button" variant="outline" size="sm">
                    <Filter className="h-4 w-4" />
                    Refine View
                  </Button>
                  <Button id="ui-style-guide-table-open-button" type="button" size="sm">
                    <Pencil className="h-4 w-4" />
                    Open Work Queue
                  </Button>
                </div>
              }
              emptyTitle="No results"
              emptyDescription="Adjust filters and try again."
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Example Usage</CardTitle>
          <CardDescription>Copy this shape when you build a new workbench instead of inventing a fresh toolbar pattern.</CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="overflow-x-auto rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] p-4 text-xs leading-6 text-[var(--neutral-gray-120)]">
            <code>{codeExample}</code>
          </pre>
        </CardContent>
      </Card>
    </div>
  );
};
